import { ContentCopy, Edit } from '@mui/icons-material';
import { Box, FormControlLabel, Grid, IconButton, InputAdornment, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import Fieldset from '@/components/Fieldset';
import { CreateExerciseDTO, ExerciseConfig, ExerciseConfigExecutionTypeEnum, ExerciseDTO } from '@/generated';
import { numberValue } from '@/utils/number-utils';
import { IntervalDialog } from '@/components/IntervalDialog';
import { ExecutionTypeDropdown } from '@/components/ExecutionTypeDropdown';

interface ExerciseProps {
  exercise: ExerciseDTO | CreateExerciseDTO;
  exerciseConfig: ExerciseConfig;
  updateExerciseConfig: (exerciseConfig: ExerciseConfig) => void;
  onCopy?: () => void;
}

export const ExerciseConfigComponent = (props: ExerciseProps) => {
  const [intervalDialogOpen, setIntervalDialogOpen] = useState(false);
  const exerciseConfig = props.exerciseConfig;
  const updateExerciseConfig = props.updateExerciseConfig;
  const isUniLateral = props.exercise.isUniLateral ?? false;
  const executionType = props.exerciseConfig.executionType ?? ExerciseConfigExecutionTypeEnum.Reps;
  const timing = props.exerciseConfig.timing ?? { intervals: [] };

  useEffect(() => {
    if (executionType !== ExerciseConfigExecutionTypeEnum.Intervals && !exerciseConfig.value) {
      updateExerciseConfig({ ...exerciseConfig, value: 10 });
    }
  }, [executionType, exerciseConfig, updateExerciseConfig]);

  const updateTiming = (changes: Partial<typeof timing>) => {
    props.updateExerciseConfig({ ...props.exerciseConfig, timing: { ...timing, ...changes } });
  };

  const copyButton = props.onCopy ? (
    <Tooltip title="Copy training settings from a progression or regression">
      <IconButton aria-label="Copy training settings" sx={{ color: 'common.white', flexShrink: 0 }} onClick={props.onCopy}>
        <ContentCopy />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <ExecutionTypeDropdown exerciseConfig={exerciseConfig} updateExerciseConfig={updateExerciseConfig} />
          {isUniLateral && (
            <FormControlLabel
              sx={{ flexShrink: 0 }}
              control={
                <Switch
                  checked={props.exerciseConfig.trackPerSide ?? false}
                  onChange={(event) => props.updateExerciseConfig({ ...props.exerciseConfig, trackPerSide: event.target.checked })}
                  name="limb-tracking"
                />
              }
              label="Limb tracking"
            />
          )}
          {copyButton}
        </Stack>
      </Grid>

      <Grid size={12}>
        <TextField
          label="Bodyweight percentage"
          aria-label="Bodyweight percentage"
          type="number"
          fullWidth
          value={props.exerciseConfig.bodyweightPercentage ?? ''}
          helperText="Enter 1–100. A value of 0 removes the percentage."
          slotProps={{
            htmlInput: { min: 0, max: 100, step: 1 },
            input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
          }}
          onChange={(event) => {
            const value = Number(event.target.value);
            props.updateExerciseConfig({
              ...props.exerciseConfig,
              bodyweightPercentage: !event.target.value || value <= 0 ? undefined : Math.min(value, 100),
            });
          }}
        />
      </Grid>

      {executionType === ExerciseConfigExecutionTypeEnum.Reps && (
        <Grid size={12} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <TextField
            label="Repetitions"
            type="number"
            fullWidth
            value={props.exerciseConfig.value ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...props.exerciseConfig, value: numberValue(event.target.value) })}
          />
          <TextField
            label="Weight"
            type="number"
            fullWidth
            value={props.exerciseConfig.weight ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...props.exerciseConfig, weight: numberValue(event.target.value) })}
          />
        </Grid>
      )}

      {executionType === ExerciseConfigExecutionTypeEnum.Duration && (
        <Grid size={12} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <TextField
            label="Duration seconds"
            type="number"
            fullWidth
            value={props.exerciseConfig.value ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...props.exerciseConfig, value: numberValue(event.target.value) })}
          />
          <TextField
            label="Weight"
            type="number"
            fullWidth
            value={props.exerciseConfig.weight ?? 0}
            onChange={(event) => props.updateExerciseConfig({ ...props.exerciseConfig, weight: numberValue(event.target.value) })}
          />
        </Grid>
      )}

      {executionType === ExerciseConfigExecutionTypeEnum.Intervals && (
        <Grid size={12}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mb: 2 }}>
            <TextField
              label="Weight"
              type="number"
              fullWidth
              value={props.exerciseConfig.weight ?? 0}
              onChange={(event) => props.updateExerciseConfig({ ...props.exerciseConfig, weight: numberValue(event.target.value) })}
            />
          </Box>
          <Fieldset title="Intervals" sx={{ m: 0, p: 2 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: props.exerciseConfig.trackPerSide ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' },
                  gap: 2,
                }}
              >
                <TextField
                  label="Init time"
                  type="number"
                  fullWidth
                  value={timing.initTimeSeconds ?? 0}
                  onChange={(event) => updateTiming({ initTimeSeconds: numberValue(event.target.value) })}
                />
                {props.exerciseConfig.trackPerSide && (
                  <TextField
                    label="Switch time"
                    type="number"
                    fullWidth
                    value={timing.switchPauseSeconds ?? 0}
                    onChange={(event) => updateTiming({ switchPauseSeconds: numberValue(event.target.value) })}
                  />
                )}
              </Box>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Table size="small" aria-label="Exercise intervals" sx={{ maxWidth: 360 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell align="right">Pause</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timing.intervals.map((interval, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell align="right">{interval.durationSeconds}s</TableCell>
                        <TableCell align="right">{interval.pauseAfterSeconds ?? 0}s</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Tooltip title="Edit intervals">
                  <IconButton aria-label="Edit intervals" sx={{ color: 'common.white', flexShrink: 0 }} onClick={() => setIntervalDialogOpen(true)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Fieldset>
          {intervalDialogOpen && (
            <IntervalDialog
              open
              intervals={timing.intervals.map((interval) => ({ ...interval }))}
              onCancel={() => setIntervalDialogOpen(false)}
              onSave={(intervals) => {
                updateTiming({ intervals });
                setIntervalDialogOpen(false);
              }}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};
